import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useProgram } from '../../../../../../hooks/useProgram';
import { Channel, ChannelLogoType, Program } from '../../../../../../interfaces';
import { useAuth } from '../../../../../../utils/auth-utils';
import fallbackImage from './tv-placeholder-landscape.png';
import channelPermissions from '../../../../../../utils/channelPermissions';
import { imageScaleValues, routes } from '../../../../../../config';
import { getImageUrlDimensions } from '../../../../../../utils/altiboxassetUtils';
import { Link } from '../../../../../../components/Link/Link';
import { getChannelLogoUrl } from '../../../../../../utils/tvUtils';
import { useTranslation } from 'react-i18next';
import { goToLogin } from '../../../../../../api/auth';
import css from './program-card.module.scss';
import { SvgLock } from '../../../../../../icons';
import { classes } from '../../../../../../utils/commonUtils';

interface ProgramCardProps {
  channel: Channel;
  program: Program;
  order: number;
}

export function ProgramCard({ channel, program, order }: ProgramCardProps) {
  const { getProgress } = useProgram(program);
  const auth = useAuth();

  function getProgramPicture(picture: Program['picture']) {
    return picture ? (window.document.body.offsetWidth > 767 ? picture.poster : picture.deflate) : null;
  }

  const [posterUrl, setPosterUrl] = useState(fallbackImage);
  const [failedToLoadDoNotLoadAgain, setFailedToLoadDoNotLoadAgain] = useState(false);
  const [progress, setProgress] = useState(getProgress());
  const progressRefreshIntervalRef = useRef<NodeJS.Timer>();

  const isLocked = useMemo(() => {
    const permission = channelPermissions(channel, auth);
    return permission.locked;
  }, [auth, channel]);

  const liveUrl = useMemo(() => {
    return `${routes.unmanaged.base}${routes.tv.base}/${channel.contentId}`;
  }, [channel.contentId]);

  useEffect(() => {
    progressRefreshIntervalRef.current = setInterval(() => {
      setProgress(getProgress());
    }, 15 * 1000);

    return () => {
      clearInterval(progressRefreshIntervalRef.current);
    };
  }, [getProgress]);

  const checkChannelPermissions = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (auth.isGuest || (channel.channelPermissions.onlyInsideforLiveTV && !auth.isHome)) {
        event.preventDefault();
      }
      return true;
    },
    [auth, channel.channelPermissions.onlyInsideforLiveTV],
  );

  // after placeholder is loaded load the poster or ad img based on width of screen
  const handlePlaceholderLoaded = () => {
    const programPicture = getProgramPicture(program.picture);
    let posterURL = fallbackImage;

    if (!failedToLoadDoNotLoadAgain && programPicture) {
      if (order > 0) {
        posterURL = getImageUrlDimensions(programPicture, imageScaleValues.live);
      } else {
        posterURL = programPicture;
      }
    }

    setPosterUrl(posterURL);
  };

  const handleLoadingError = () => {
    if (!failedToLoadDoNotLoadAgain) {
      setFailedToLoadDoNotLoadAgain(true);
      setPosterUrl(fallbackImage);
    }
  };

  if (!channel) {
    return null;
  }

  return (
    <Link className={css.container} to={liveUrl} onClick={(e) => checkChannelPermissions(e)}>
      {isLocked ? <SvgLock width={24} height={24} className={css.locked} /> : null}

      <img
        className={css.image}
        rel="preload"
        alt={program.name}
        src={posterUrl}
        onError={handleLoadingError}
        onLoad={handlePlaceholderLoaded}
      />

      <ProgramOverlay channel={channel} />

      <h3 className={css.title}>{program.name}</h3>
      <img className={css.logo} alt={channel.name} src={getChannelLogoUrl(channel, ChannelLogoType.CROPPED)} />
      <progress className={css.progress} value={progress} />
    </Link>
  );
}

interface ProgramOverlayProps {
  channel: Channel;
}

function ProgramOverlay({ channel }: ProgramOverlayProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const onlyInsideforLiveTV = channel.channelPermissions.onlyInsideforLiveTV;

  if (auth.isGuest) {
    return (
      <div className={classes(css.overlay, css.disabled)}>
        {t('log in to see this channel')}
        <button onClick={goToLogin} className="login">
          <span className="aib-icon">q</span>
          {t('log on')}
        </button>
      </div>
    );
  }

  if (onlyInsideforLiveTV && !auth.isHome) {
    return <div className={classes(css.overlay, css.disabled)}>{t("can't show outside home")}</div>;
  }

  return (
    <div className={css.overlay}>
      <div className="aib-icon">z</div>
    </div>
  );
}

function ProgramCardSkeleton() {
  return <div className={classes(css.container, css.skeleton)} />;
}

ProgramCard.Skeleton = ProgramCardSkeleton;
